/**
 * The origin-relative model-view-projection matrix (pure, float64 math).
 *
 * MapLibre v6 hands custom layers two float64, column-major matrices:
 *   modelViewProjectionMatrix           world pixels (0..worldSize, z in metres) → clip
 *   defaultProjectionData.mainMatrix    Mercator 0..1 → clip (= MVP · S(worldSize))
 * Our vertices are Web-Mercator deltas from a local origin (pack.ts), so
 *
 *   M_rel = M · T(ox·ws, oy·ws, 0) · S(ws, ws, 1)      (ws = 1 for mainMatrix)
 *
 * is built in float64 here and only then rounded to float32 for the GPU. The
 * large translation never meets float32, which is what keeps a 130 km route
 * steady at z15 (no vertex jitter while the camera moves).
 */

/** Column-major 4×4 in, column-major float32 out. */
export function relativeMatrix<T extends Float32Array | Float64Array = Float32Array>(
  mvp: ArrayLike<number>,
  worldSize: number,
  originX: number,
  originY: number,
  out: T = new Float32Array(16) as T,
): T {
  const ws = worldSize;
  const tx = originX * ws, ty = originY * ws;
  for (let r = 0; r < 4; r++) {
    const c0 = mvp[r], c1 = mvp[4 + r], c2 = mvp[8 + r], c3 = mvp[12 + r];
    out[r] = c0 * ws; //        column 0 scaled
    out[4 + r] = c1 * ws; //    column 1 scaled
    out[8 + r] = c2; //         column 2 (z) untouched
    out[12 + r] = c0 * tx + c1 * ty + c3; // translation, in float64 before the store
  }
  return out;
}

/** Transform (x, y, 0, 1) by a column-major matrix (float64). */
export function transformPoint(m: ArrayLike<number>, x: number, y: number): [number, number, number, number] {
  return [
    m[0] * x + m[4] * y + m[12],
    m[1] * x + m[5] * y + m[13],
    m[2] * x + m[6] * y + m[14],
    m[3] * x + m[7] * y + m[15],
  ];
}

/**
 * Device px per CSS px that the map actually renders at. MapLibre clamps its canvas to maxCanvasSize
 * (default 4096 × 4096) by rendering below devicePixelRatio, e.g. a 2560 CSS px wide map at 2× → 1.6×.
 * map.getPixelRatio() still returns the unclamped value, which would draw every width, glow and comet
 * 25 % too large. `cssWidth` is the canvas's CSS width (MapLibre writes canvas.style.width).
 */
export function effectivePixelRatio(bufferWidth: number, cssWidth: number, fallback: number): number {
  return bufferWidth > 0 && cssWidth > 0 && Number.isFinite(cssWidth) ? bufferWidth / cssWidth : fallback;
}

/** Metres per CSS pixel → CSS pixels per metre at `latDeg` for a Web-Mercator world of `worldSize` px. */
export function pxPerMetre(worldSize: number, latDeg: number): number {
  const EARTH_CIRCUMFERENCE = 40075016.686;
  return worldSize / (EARTH_CIRCUMFERENCE * Math.cos((latDeg * Math.PI) / 180));
}
